import React, { useState, useEffect } from "react";
import { createEventType, updateEventType } from "../../../Services/DynamicEventTypes";
import { DEFAULT_EVENT_TYPE_FORM } from "../constants";
import { DYNAMIC_EVENT_TYPE_STRINGS, getGeneratedQuestionsLabel, SMART_PREFILL_STRINGS } from "../strings";

const EventTypeForm = ({
    eventType,
    onClose,
    initialValues,
    prefillQuestions,
    prefillImage,
    prefillImageLoading,
    prefillImageError,
    onCreateQuestions
}) => {
    const isEdit = !!eventType;
    
    const [form, setForm] = useState({ ...DEFAULT_EVENT_TYPE_FORM });
    
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [prefillImageApplied, setPrefillImageApplied] = useState(false);
    const generatedQuestionsCount = Array.isArray(prefillQuestions) ? prefillQuestions.length : 0;
    const shouldCreateQuestions = !isEdit && generatedQuestionsCount > 0 && typeof onCreateQuestions === "function";

    useEffect(() => {
        if (eventType) {
            setForm({
                display_name: eventType.display_name || DEFAULT_EVENT_TYPE_FORM.display_name,
                emoji: eventType.emoji || DEFAULT_EVENT_TYPE_FORM.emoji,
                variant_color: eventType.variant_color || DEFAULT_EVENT_TYPE_FORM.variant_color,
                min_estimated_guests: eventType.min_estimated_guests || DEFAULT_EVENT_TYPE_FORM.min_estimated_guests,
                max_estimated_guests: eventType.max_estimated_guests || DEFAULT_EVENT_TYPE_FORM.max_estimated_guests,
                default_estimated_guests: eventType.default_estimated_guests || DEFAULT_EVENT_TYPE_FORM.default_estimated_guests,
                show_participant_names: eventType.show_participant_names !== false,
                swappable_participant_names: eventType.swappable_participant_names || DEFAULT_EVENT_TYPE_FORM.swappable_participant_names,
                sort_order: eventType.sort_order || DEFAULT_EVENT_TYPE_FORM.sort_order,
                is_active: typeof eventType.is_active === "boolean" ? eventType.is_active : DEFAULT_EVENT_TYPE_FORM.is_active,
                variant_strings: eventType.variant_strings || DEFAULT_EVENT_TYPE_FORM.variant_strings,
                event_date: eventType.event_date
                    ? (typeof eventType.event_date === "string" && eventType.event_date.length >= 10
                        ? eventType.event_date.slice(0, 10)
                        : new Date(eventType.event_date).toISOString().slice(0, 10))
                    : ""
            });
            // Set preview if placeholder image exists
            if (eventType.placeholder_image_url) {
                setImagePreview(eventType.placeholder_image_url);
            }
        }
    }, [eventType]);

    useEffect(() => {
        if (!eventType && initialValues) {
            setForm({ ...DEFAULT_EVENT_TYPE_FORM, ...initialValues });
        }
    }, [eventType, initialValues]);

    useEffect(() => {
        if (prefillImage) {
            setPrefillImageApplied(false);
        }
    }, [prefillImage]);

    useEffect(() => {
        if (isEdit) {
            return;
        }
        if (!prefillImage || prefillImageApplied || imageFile) {
            return;
        }
        if (prefillImage.file && prefillImage.previewUrl) {
            setImageFile(prefillImage.file);
            setImagePreview(prefillImage.previewUrl);
            setPrefillImageApplied(true);
        }
    }, [prefillImage, prefillImageApplied, imageFile, isEdit]);

    // Auto-generate internal name from display name
    const generateInternalName = (displayName) => {
        return displayName.replace(/\s+/g, '').replace(/[^a-zA-Z0-9]/g, '');
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
                setError("Please upload an image in JPG or PNG format");
                return;
            }
            // Validate file size (20MB max)
            if (file.size > 20 * 1024 * 1024) {
                setError("Image size must be less than 20MB");
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setError(null);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (prefillImage) {
            setPrefillImageApplied(true);
        }
    };

    const handleSave = async () => {
        if (!form.display_name || !form.variant_color) {
            setError("Display name and color are required");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const payload = {
                ...form,
                name: generateInternalName(form.display_name)
            };
            let response = null;

            // If image file is present, use FormData
            if (imageFile) {
                const formData = new FormData();
                Object.keys(payload).forEach(key => {
                    if (key === 'variant_strings') {
                        formData.append(key, JSON.stringify(payload[key]));
                    } else {
                        formData.append(key, payload[key]);
                    }
                });
                formData.append('image', imageFile);

                if (isEdit) {
                    response = await updateEventType(eventType.id, formData);
                } else {
                    response = await createEventType(formData);
                }
            } else {
                // No image, use regular JSON payload
                if (isEdit) {
                    response = await updateEventType(eventType.id, payload);
                } else {
                    response = await createEventType(payload);
                }
            }

            if (!isEdit && shouldCreateQuestions) {
                const createdEventType = response?.data?.data?.responseData?.[0];
                if (!createdEventType?.id) {
                    onClose({ saved: true, warning: SMART_PREFILL_STRINGS.warningQuestionsFailed });
                    return;
                }
                try {
                    await onCreateQuestions(createdEventType.id);
                } catch (questionError) {
                    onClose({ saved: true, warning: SMART_PREFILL_STRINGS.warningQuestionsFailed });
                    return;
                }
            }

            onClose(true); // Close and trigger reload
        } catch (err) {
            console.error('Error saving event type:', err);
            setError(err.response?.data?.message || "Failed to save event type");
            setSaving(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {isEdit ? `Edit Event Type: ${eventType.display_name}` : 'Create New Event Type'}
                        </h5>
                        <button type="button" className="btn-close" onClick={() => onClose(false)}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}
                        
                        <div className="alert alert-info">
                            <small>Custom event types are only available in the Five On app. Upload a placeholder image that will be shown when users haven't uploaded an event image yet.</small>
                        </div>
                        {prefillImageLoading && (
                            <div className="alert alert-info">
                                <small>{DYNAMIC_EVENT_TYPE_STRINGS.webImageLoading}</small>
                            </div>
                        )}
                        {prefillImageError && (
                            <div className="alert alert-warning">
                                <small>{prefillImageError}</small>
                            </div>
                        )}
                        {!isEdit && generatedQuestionsCount > 0 && (
                            <div className="alert alert-secondary">
                                <small>{getGeneratedQuestionsLabel(generatedQuestionsCount)}</small>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Placeholder Image</label>
                            {imagePreview && (
                                <div className="mb-2">
                                    <img 
                                        src={imagePreview} 
                                        alt="Placeholder preview" 
                                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #dee2e6' }}
                                    />
                                    <div className="mt-2">
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={handleRemoveImage}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                </div>
                            )}
                            <input
                                type="file"
                                className="form-control"
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleImageChange}
                            />
                            <small className="text-muted">JPG or PNG format, max 20MB. This image will be used as the default placeholder for events of this type.</small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Display Name *</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g., Watch Party"
                                value={form.display_name}
                                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                            />
                            <small className="text-muted">
                                Internal name: {generateInternalName(form.display_name) || '(auto-generated)'}
                            </small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Event Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={form.event_date || ""}
                                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                            />
                            <small className="text-muted">Optional.</small>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Emoji</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔌"
                                    value={form.emoji}
                                    onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                                    maxLength={10}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Color *</label>
                                <div className="d-flex gap-2">
                                    <input
                                        type="color"
                                        className="form-control form-control-color"
                                        value={form.variant_color}
                                        onChange={(e) => setForm({ ...form, variant_color: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={form.variant_color}
                                        onChange={(e) => setForm({ ...form, variant_color: e.target.value })}
                                        placeholder="#9C27B0"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Min Guests</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={form.min_estimated_guests}
                                    onChange={(e) => setForm({ ...form, min_estimated_guests: parseInt(e.target.value) })}
                                    min="1"
                                />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Max Guests</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={form.max_estimated_guests}
                                    onChange={(e) => setForm({ ...form, max_estimated_guests: parseInt(e.target.value) })}
                                    min={form.min_estimated_guests}
                                />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label">Default Guests</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={form.default_estimated_guests}
                                    onChange={(e) => setForm({ ...form, default_estimated_guests: parseInt(e.target.value) })}
                                    min={form.min_estimated_guests}
                                    max={form.max_estimated_guests}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={form.show_participant_names}
                                        onChange={(e) => setForm({ ...form, show_participant_names: e.target.checked })}
                                        id="showParticipantNames"
                                    />
                                    <label className="form-check-label" htmlFor="showParticipantNames">
                                        Show Participant Name Fields
                                    </label>
                                    <small className="d-block text-muted">If unchecked, hides bride/groom name input fields</small>
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={form.swappable_participant_names}
                                        onChange={(e) => setForm({ ...form, swappable_participant_names: e.target.checked })}
                                        id="swappableNames"
                                        disabled={!form.show_participant_names}
                                    />
                                    <label className="form-check-label" htmlFor="swappableNames">
                                        Swappable Participant Names
                                    </label>
                                    <small className="d-block text-muted">If checked, shows N dynamic participant name fields instead of bride/groom</small>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Sort Order</label>
                            <input
                                type="number"
                                className="form-control"
                                value={form.sort_order}
                                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) })}
                            />
                            <small className="text-muted">Controls display order in event type selector</small>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{DYNAMIC_EVENT_TYPE_STRINGS.statusLabel}</label>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    id="eventTypeActive"
                                />
                                <label className="form-check-label" htmlFor="eventTypeActive">
                                    {DYNAMIC_EVENT_TYPE_STRINGS.activeLabel}
                                </label>
                            </div>
                            <small className="text-muted">{DYNAMIC_EVENT_TYPE_STRINGS.statusHelp}</small>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => onClose(false)} disabled={saving}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventTypeForm;
